/**
 * useRentals Hook
 * Epic 14: Bérlés kezelés
 * Fetches rental data from API with loading and error states
 */

import {
  getRentalById,
  getRentalExpirations,
  getRentals,
  getRentalStatistics,
  previewSettlement,
  type Rental,
  type RentalExpiration,
  type RentalFilters,
  type RentalStatistics,
  type SettlementPreviewItemInput,
} from '@/api/rentals';
import { SettlementPreviewWireSchema, type SettlementPreviewWire } from '@/api/wire-schemas';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseRentalsResult {
  rentals: Rental[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseRentalResult {
  rental: Rental | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseRentalStatsResult {
  stats: RentalStatistics | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch list of rentals with filters
 */
export function useRentals(filters: RentalFilters = {}): UseRentalsResult {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRentals = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getRentals(filters);
      setRentals(response.data);
      setTotal(response.meta.total);
      setPage(response.meta.page);
      setPageSize(response.meta.pageSize);
      setHasMore(response.meta.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rentals');
      setRentals([]);
    } finally {
      setLoading(false);
    }
  }, [
    filters.status,
    filters.customerId,
    filters.equipmentId,
    filters.overdueOnly,
    filters.dueFilter,
    filters.search,
    filters.page,
    filters.pageSize,
  ]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  return {
    rentals,
    total,
    page,
    pageSize,
    hasMore,
    loading,
    error,
    refetch: fetchRentals,
  };
}

/**
 * Hook to fetch a single rental by ID
 */
export function useRental(id: string | undefined): UseRentalResult {
  const [rental, setRental] = useState<Rental | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRental = useCallback(async () => {
    if (!id) {
      setRental(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getRentalById(id);
      setRental(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rental');
      setRental(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRental();
  }, [fetchRental]);

  return {
    rental,
    loading,
    error,
    refetch: fetchRental,
  };
}

/**
 * Hook to fetch rental statistics
 */
export function useRentalStats(): UseRentalStatsResult {
  const [stats, setStats] = useState<RentalStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getRentalStatistics();
        setStats(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
}

interface UseRentalExpirationsResult {
  /** A nyers értesítés-lista (lejáró/lejárt bérlések „kit hívjak" adatai). */
  expirations: RentalExpiration[];
  /** rentalId → értesítés, az OverdueRentalsPage soraira join-hoz. */
  byRentalId: Map<string, RentalExpiration>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch rental expiration notifications (partner-telefon + „kit hívjak" adatok).
 * A `GET /rental-expirations` wire-ből; a `byRentalId` Map rentalId-vel join-olja
 * az OverdueRentalsPage sorait (Epic 42 Story 42-2 / 39-kör FE-2).
 */
export function useRentalExpirations(): UseRentalExpirationsResult {
  const [expirations, setExpirations] = useState<RentalExpiration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchExpirations = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getRentalExpirations();
        if (!cancelled) setExpirations(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch rental expirations');
          setExpirations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchExpirations();

    return () => {
      cancelled = true;
    };
  }, []);

  const byRentalId = useMemo(
    () => new Map(expirations.map(exp => [exp.rentalId, exp])),
    [expirations]
  );

  return { expirations, byRentalId, loading, error };
}

// ── Settlement-preview hook (40-kör PV-BERLES-003, PR-5 pin) ──

export interface UseSettlementPreviewResult {
  preview: SettlementPreviewWire | null;
  loading: boolean;
}

/**
 * Read-only elszámolás-előnézet a pultos inputjaira. Graceful-guard (#718):
 * endpoint-hiba (pl. a PR-5 landolása előtti 404) vagy wire-drift (safeParse-fail)
 * → preview=null → a hívó nem renderel panelt. 0 FE-számítás — minden összeg a BE-ből.
 */
export function useSettlementPreview(
  rentalId: string | undefined,
  items: SettlementPreviewItemInput[],
  enabled: boolean
): UseSettlementPreviewResult {
  const [preview, setPreview] = useState<SettlementPreviewWire | null>(null);
  const [loading, setLoading] = useState(false);

  // A tömb-identitás minden renderen új — tartalom-kulccsal stabilizáljuk a dep-et.
  const itemsKey = JSON.stringify(items);

  useEffect(() => {
    if (!enabled || !rentalId || items.length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;

    const fetchPreview = async () => {
      setLoading(true);
      try {
        const res = await previewSettlement(rentalId, items);
        if (cancelled) return;
        const parsed = SettlementPreviewWireSchema.safeParse(res.data);
        setPreview(parsed.success ? parsed.data : null);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items az itemsKey-n át stabil
  }, [rentalId, itemsKey, enabled]);

  return { preview, loading };
}
